import React, { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Box, Stack, Button, Text, useToast } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { UPDATE_CANDIDATE_ROLE_PREF_MUTATION } from "../graphql/agencies";
import MoblyzeApolloClient from "../services/MoblyzeApolloClient";
import { MultiSelectInput } from "./HookFormsInputs";
import {  CREATE_TAG_MUTATION, GET_ALL_TAGS_QUERY, GET_USER_TAGS_QUERY, UPDATE_USER_TAGS_MUTATION } from "../graphql/roles_admin";
import { CLIENT_CANDIDATE_SKILLS_QUERY } from "../graphql/jobs";
import { GET_CANDIDATE_ROLES_QUERY } from "../graphql/jobs_admin";

export default function UpdateCandidateRolePrefForm({
  candidateUser,
  refetch,
}) {
  const toast = useToast();
  const [allRoleOptions, setAllRoleOptions] = React.useState([]);
  const [allTagOptions, setAllTagOptions] = React.useState([]);
  const [roleSkills, setRoleSkills] = React.useState({});
  const { data: candidateRolesAndSkills } = useQuery(
    CLIENT_CANDIDATE_SKILLS_QUERY,
    {
      variables: {
        candidateProfileId: candidateUser?.candidateProfile?.id,
      },
      skip: !candidateUser?.candidateProfile?.id,
    }
  );
  const defaultRoles = useMemo(() =>
    candidateUser?.candidateProfile?.roles?.map((role) => ({
      value: role.id,
      label: role.name,
    })) || [],
    [candidateUser?.candidateProfile?.roles]
  );

  const { data: rolesAndSkills } = useQuery(
    GET_CANDIDATE_ROLES_QUERY
  );
  const { data: tagsData } = useQuery(
    GET_ALL_TAGS_QUERY
  );
  const { data: userTagsData } = useQuery(GET_USER_TAGS_QUERY, {
    variables: {
      userId: candidateUser?.userId,
    },
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      roles: defaultRoles,
      tags: [],
      skills: [],
    },
    mode: "onChange", // This enables real-time validation
  });
  const [skillOptions, setSkillOptions] = React.useState([]);

  const selectedRoles = useWatch({
    control,
    name: "roles",
    defaultValue: defaultRoles,
  });

  useEffect(() => {
    const selectedRoleIds = selectedRoles?.map((role) => role.value) || [];

    if (selectedRoleIds.length > 0) {
      const allowedSkills = [];
      for (let roleId of selectedRoleIds) {
        if (roleSkills[roleId]) {
          allowedSkills.push(...roleSkills[roleId]);
        }
      }
      setSkillOptions(allowedSkills);
      const allowedSkillIds = allowedSkills.map((skill) => skill.value);
      setValue(
        "skills",
        getValues("skills")?.filter((skill) =>
          allowedSkillIds.includes(skill.value)
        ) || []
      );
    } else {
      setSkillOptions([]);
      setValue("skills", []);
    }
  }, [selectedRoles, roleSkills, setSkillOptions, setValue, getValues]);

  useEffect(() => {
    if (
      candidateRolesAndSkills?.getCandidateSkillsByProfileId &&
      candidateRolesAndSkills.getCandidateSkillsByProfileId.length > 0
    ) {
      const defaultSkills = [];
      for (let role of candidateRolesAndSkills.getCandidateSkillsByProfileId) {
        defaultSkills.push(
          ...role.skills.map((skill) => ({
            value: skill.id,
            label: skill.name,
          }))
        );
      }
      setValue("skills", defaultSkills);
    } else {
      setValue("skills", []);
    }
  }, [candidateRolesAndSkills, setValue]);

  useEffect(() => {
    if (
      rolesAndSkills?.candidateRoles &&
      rolesAndSkills?.candidateRoles.length > 0
    ) {
      let roles = [];
      let skills = {};
      for (let role of rolesAndSkills.candidateRoles) {
        roles.push({
          value: role.id,
          label: role.name,
        });
        skills[role.id] = role.requiredSkills.map((skill) => ({
          value: skill.id,
          label: skill.name,
        }));
      }
      setAllRoleOptions(roles);
      setRoleSkills(skills);
      if (skillOptions.length === 0 && defaultRoles?.length) {
        const allowedSkills = [];
        for (let roleId of defaultRoles.map((role) => role.value)) {
          if (skills[roleId]) {
            allowedSkills.push(...skills[roleId]);
          }
        }
        setSkillOptions(allowedSkills);
      }
    }
  }, [rolesAndSkills, defaultRoles, skillOptions]);

  useEffect(() => {
    if (tagsData?.tags?.length > 0) {
      const tags = tagsData.tags.map(tag => ({
        value: tag.id,
        label: tag.name,
      }));
      setAllTagOptions(tags);
    }
  }, [tagsData]);

  useEffect(() => {
    const userTagIds = userTagsData?.userTags?.map(tag => tag.tagId) || [];
    const defaultUserTags = tagsData?.tags
      ?.filter(i => userTagIds.includes(i.id))
      ?.map(tag => ({
        value: tag.id,
        label: tag.name,
      })) || [];
    setValue("tags", defaultUserTags);
  }, [tagsData, userTagsData, setValue]);

  const onFormSubmit = async () => {
    try {
      const roleIds = getValues("roles").map(role => role.value);
      const skillIds = getValues("skills").map(skill => skill.value);
      const tagIds = getValues("tags").map(tag => tag.value);

      const skillInput = [];
      const inputtedSkills = [];
      for (let roleId of roleIds) {
        const input = { roleId: roleId, skillIds: [] };
        if (roleSkills[roleId]) {
          for (let skillId of skillIds) {
            if (
              roleSkills[roleId]
                .map((skill) => skill.value)
                .includes(skillId) &&
              !inputtedSkills.includes(skillId)
            ) {
              input.skillIds.push(skillId);
              inputtedSkills.push(skillId);
            }
          }
        }
        if (input.skillIds.length > 0) {
          skillInput.push(input);
        }
      }
      await MoblyzeApolloClient.mutate({
        mutation: UPDATE_CANDIDATE_ROLE_PREF_MUTATION,
        variables: {
          candidateProfileId: candidateUser.candidateProfileId,
          roleIds: roleIds,
          skillIds: skillInput,
        },
      });

      await MoblyzeApolloClient.mutate({
        mutation: UPDATE_USER_TAGS_MUTATION,
        variables: {
          userId: candidateUser.userId,
          tagIds: tagIds,
        },
      });

      if (refetch) {
        refetch();
      }

      toast({
        title: "The roles, skills, and tags have been updated",
        status: "success",
      });

      setTimeout(() => {
        window.location.reload();
      }, 500);

    } catch (e) {
      toast({
        title: e?.message || JSON.stringify(e),
        status: "error",
      });
    }
  };

  const handleCreateTag = async (inputValue) => {
    try {
      const { data } = await MoblyzeApolloClient.mutate({
        mutation: CREATE_TAG_MUTATION,
        variables: {
          name: inputValue,
        },
      });

      const newTag = {
        value: data?.createTag?.id,
        label: data?.createTag?.name,
      };

      // Add the new tag to allTagOptions
      setAllTagOptions((prev) => [...prev, newTag]);

      return newTag;
    } catch (err) {
      toast({
        title: err?.message || "Failed to create tag",
        status: "error",
      });
      return null;
    }
  };


  return (
    <Box borderRadius="5px" background="gray.800" p="44px 20px" width="100%">
      <Text
        color={"white"}
        fontSize={"28px"}
        fontWeight={600}
        lineHeight={"40px"}
      >
        Edit Candidate Roles and Skills
      </Text>
      <Stack spacing={4} mt="8" width="100%">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <MultiSelectInput
            name="roles"
            label="Role"
            required
            options={allRoleOptions}
            register={register}
            control={control}
            errors={errors}
            ml={"0"}
            mb={"32px"}
          />

          <MultiSelectInput
            name="skills"
            label="Skills"
            options={skillOptions}
            register={register}
            control={control}
            errors={errors}
            ml={"0"}
            mb={"32px"}
          />

          <MultiSelectInput
            isCreatable={true}
            name="tags"
            label="Tags"
            options={allTagOptions}
            register={register}
            control={control}
            errors={errors}
            ml={"0"}
            mb={"32px"}
            onCreateOption={handleCreateTag}
          />

          <Button width="100%" type="submit">
            Update Candidate Roles and Skills
          </Button>
        </form>
      </Stack>
    </Box>
  );
}
